"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../css/addDoctore.module.css";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import FilterSearch from "../../../../components/FilterSearch(name)";
import FilterButtons from "../../../../components/universal-filter-tabs";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import PractitionerDetailsModal from "../componenets/views/PractitionerDetailsModal.jsx";
import { 
  listPractitioners as apiListPractitioners, 
  getPractitioner as apiGetPractitioner, 
  setPractitionerEnabled as apiSetPractitionerEnabled 
} from "../../../../api/profile/add_doctor";

export default function AddDoctor() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const isDirector = roleId === 2;
  const location = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);

  const [tableRows, setTableRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");

  const [doctorForm, setDoctorForm] = useState({
    surname: "",
    initial: "",
    firstNames: "",
    title: "Dr",
    contact: "",
    practiceContact: "",
    postalStreet: "",
    postalSuburb: "",
    postalCity: "",
    postalPostalCode: "",
    practiceStreet: "",
    practiceSuburb: "",
    practiceCity: "",
    practicePostalCode: "",
    postal1: "",
    postal2: "",
    postal3: "",
    practice1: "",
    practice2: "",
    practice3: "",
    practice4: "",
    email: "",
    practiceNumber: "",
    speciality: "",
    practiceType: "",
    practiceTypeOther: "",
    practiceTypeId: null,
  });

  const refreshPractitioners = async () => {
    try {
      const rows = await apiListPractitioners();
      const mapped = rows.map((r) => ({
        id: r?.practitionerid,
        practitioner: `${r?.title ? r.title + " " : ""}${r?.firstname ?? ""} ${r?.surname ?? ""}`.trim(),
        practiceNumber: r?.practicenumber ?? "",
        address: r?.practiceaddress ?? "",
        enabled: r?.enabled !== false,
      }));
      setTableRows(mapped);
    } catch (_) {
      setTableRows([]);
    }
  };

  const renderCell = (value, row) => {
    const enabled = row?.enabled !== false;
    const className = enabled ? undefined : styles.disabledCellText;
    return <span className={className}>{value}</span>;
  };

  const columns = useMemo(
    () => [
      { header: "Practitioner", accessor: "practitioner", render: (value, row) => renderCell(value, row) },
      { header: "Practice Number", accessor: "practiceNumber", render: (value, row) => renderCell(value, row) },
      { header: "Address", accessor: "address", render: (value, row) => renderCell(value, row) },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => {
          const enabled = row?.enabled !== false;
          const className = enabled ? undefined : styles.disabledCellText;
          return <span className={className}>{enabled ? "Enabled" : "Disabled"}</span>;
        },
      },
      {
        header: "Action",
        accessor: "action",
        render: (_val, row, rowIndex) => (
          <Button
            variant="primary"
            className={styles.viewBtnSized}
            onClick={async () => {
              setSelectedIndex(rowIndex);
              setSelectedPractitioner({ id: row.id, enabled: !!row.enabled });
              try {
                const d = await apiGetPractitioner(row.id);
                setSelectedPractitioner({ id: row.id, enabled: d?.enabled !== false });
                const practiceAddress = d?.practiceaddress || "";
                const postalAddress = d?.postaladdress || "";
                const pParts = String(practiceAddress).split(",").map((s) => s.trim());
                const poParts = String(postalAddress).split(",").map((s) => s.trim());
                const nameTitle = d?.title || "Dr";
                const firstname = d?.firstname || "";
                const surname = d?.surname || "";
                setDoctorForm((prev) => ({
                  ...prev,
                  surname: surname || "",
                  initial: (firstname || "").slice(0, 1),
                  firstNames: firstname || "",
                  title: nameTitle,
                  contact: d?.contactnumber || "",
                  practiceContact: d?.practicecontactnumber || "",
                  postalStreet: poParts[0] || "",
                  postalSuburb: poParts[1] || "",
                  postalCity: poParts[2] || "",
                  postalPostalCode: poParts[3] || "",
                  email: d?.email || "",
                  practiceNumber: d?.practicenumber || "",
                  speciality: d?.speciality || "",
                  practiceType: d?.practicetype?.practicetype || "",
                  practiceTypeOther: "",
                  practiceStreet: pParts[0] || "",
                  practiceSuburb: pParts[1] || "",
                  practiceCity: pParts[2] || "",
                  practicePostalCode: pParts[3] || "",
                  practiceTypeId: d?.practicetypeid || d?.practicetype?.practicetypeid || null,
                }));
                setIsModalOpen(true);
              } catch (_) {
                setIsModalOpen(true);
              }
            }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const filteredRows = useMemo(
    () => {
      const bySearch = tableRows.filter(
        (r) =>
          r.practitioner.toLowerCase().includes(search.toLowerCase()) ||
          r.practiceNumber.toLowerCase().includes(search.toLowerCase()) ||
          r.address.toLowerCase().includes(search.toLowerCase())
      );

      return bySearch.filter((r) => {
        const enabled = r?.enabled !== false;
        if (statusFilter === "Active") return enabled;
        if (statusFilter === "Inactive") return !enabled;
        return true;
      });
    },
    [search, tableRows, statusFilter]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    refreshPractitioners();
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            <div className={styles.headerRow}></div>
            <div className={styles.toolbarRow}>
              <div className={styles.filterCard}>
                <FilterSearch
                  filterName="Filter"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search Name"
                  wrapperStyle={styles.filterSearchWrapper}
                  labelStyle={styles.filterSearchLabel}
                  searchContainerStyle={styles.filterSearchContainer}
                />
                <FilterButtons
                  buttons={["All", "Active", "Inactive"]}
                  activeButton={statusFilter}
                  onButtonClick={setStatusFilter}
                />
              </div>

              {!isDirector && (
                <Button
                  onClick={() => {
                    setDoctorForm({
                      surname: "",
                      initial: "",
                      firstNames: "",
                      title: "Dr",
                      contact: "",
                      practiceContact: "",
                      postal1: "",
                      postal2: "",
                      postal3: "",
                      email: "",
                      practiceNumber: "",
                      speciality: "",
                      practiceType: "",
                      practiceTypeOther: "",
                      practice1: "",
                      practice2: "",
                      practice3: "",
                      practice4: "",
                      practiceTypeId: null,
                    });
                    setSelectedIndex(-1);
                    setSelectedPractitioner(null);
                    setIsModalOpen(true);
                  }}
                  className={styles.addBtn}
                >
                  + Add Practitioner
                </Button>
              )}
            </div>

            <Table columns={columns} data={pageData} />
            <div className={styles.paginationContainer}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>

            <PractitionerDetailsModal
              isOpen={isModalOpen && !showToggleConfirm}
              onClose={() => {
                setIsModalOpen(false);
                if (location?.state?.fromCreateProfile) {
                  navigate("/customer/profile/create", { state: { restoreCreateDraft: true } });
                }
              }}
              doctorForm={doctorForm}
              setDoctorForm={setDoctorForm}
              practitionerId={selectedPractitioner?.id || null}
              onSaved={async () => {
                await refreshPractitioners();
                setIsModalOpen(false);
                if (location?.state?.fromCreateProfile) {
                  const pn = (doctorForm?.practiceNumber || "").trim();
                  navigate("/customer/profile/create", {
                    state: {
                      restoreCreateDraft: true,
                      selectPracticeNumber: pn || undefined,
                    },
                  });
                }
              }}
              onRequestDisable={!isDirector ? () => setShowToggleConfirm(true) : undefined}
              showDisable={!!selectedPractitioner && !isDirector}
              disableLabel={selectedPractitioner?.enabled === false ? "Enable" : "Disable"}
              readOnly={isDirector}
            />

            {showToggleConfirm && !isDirector && (
              <Modal onClose={() => setShowToggleConfirm(false)} closeStyle="plain">
                <div className={styles.toggleConfirmContent}>
                  <h2 className={styles.toggleConfirmTitle}>
                    {selectedPractitioner?.enabled === false ? 'Enable Practitioner' : 'Disable Practitioner'}
                  </h2>

                  <p className={styles.toggleConfirmBody}>
                    Are you sure you want to {selectedPractitioner?.enabled ? 'disable' : 'enable'} this practitioner?
                  </p>
                  <div className={styles.toggleConfirmActions}>
                    <Button
                      variant="primary"
                      className={styles.confirmYes}
                      noHover
                      onClick={async () => {
                        try {
                          if (!selectedPractitioner?.id) return;
                          const desired = !selectedPractitioner.enabled;
                          const res = await apiSetPractitionerEnabled(selectedPractitioner.id, desired);
                          if (res) {
                            await refreshPractitioners();
                            setIsModalOpen(false);
                          }
                        } finally {
                          setShowToggleConfirm(false);
                          setSelectedPractitioner(null);
                        }
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="primary"
                      className={styles.confirmCancel}
                      noHover
                      onClick={() => setShowToggleConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}